import { useEffect, useState } from 'react';

import { useGetApiV1CourseregistrationSharedCourseListMultiple } from '@/shared/api/generated';

import type { DropdownOption } from './useCompaniesDropdown';

/**
 * Busca server-side de cursos por nome, com debounce de 1 s e mínimo de 3 caracteres.
 *
 * @param filter    - Texto digitado pelo usuário no campo de busca (default: `''`).
 * @param companyId - Filtra cursos pertencentes à empresa informada (opcional).
 * @returns Lista de cursos com estado de loading e sucesso da query.
 *
 * @example
 * ```tsx
 * const { courses, isLoading, isQuerySuccess } = useCoursesDropdown(courseSearch);
 * ```
 */
export function useCoursesDropdown(filter = '', companyId?: number) {
  const [debouncedFilter, setDebouncedFilter] = useState('');

  useEffect(() => {
    if (filter === '') {
      setDebouncedFilter('');
      return;
    }
    const timer = setTimeout(() => setDebouncedFilter(filter), 1000);
    return () => clearTimeout(timer);
  }, [filter]);

  const isReady = debouncedFilter.length >= 3;

  const { data, isLoading, isSuccess } = useGetApiV1CourseregistrationSharedCourseListMultiple(
    isReady
      ? { filter: debouncedFilter, ...(companyId !== undefined ? { companyId } : {}) }
      : undefined,
    { query: { enabled: isReady } },
  );

  const courses: readonly DropdownOption[] = (data ?? []).map(item => ({
    id: item.key ?? '',
    name: item.value ?? '',
  }));

  return {
    courses,
    isLoading,
    isQuerySuccess: isSuccess,
  } as const;
}
